import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE } from "@/lib/site-data";
import { LIMO_LANDINGS } from "@/lib/commercial-landing";
import { ldJson, faqJsonLd, hoursSpec24x7 } from "@/lib/seo-schema";
import { breadcrumbJsonLd } from "@/lib/breadcrumbs";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { HourlyRates } from "@/components/HourlyRates";
import { ContactButtons } from "@/components/ContactButtons";
import { CTABand } from "@/components/CTA";
import heroImg from "@/assets/fleet-hero-lineup.jpg";

const SITE_URL = "https://premierexecutivechauffeur.com";
const URL = `${SITE_URL}/limo-service`;
const OG_IMAGE = `${SITE_URL}/og-default.jpg`;
const DESCRIPTION =
  "Limo service across the Bay Area: San Jose, San Francisco, Palo Alto, Santa Clara and Oakland. Flat-rate airport transfers, hourly from $130/hr.";

const HUB_FAQS = [
  {
    q: "What areas does your limo service cover?",
    a: "We operate across Silicon Valley, the Peninsula, San Francisco, the East Bay, Santa Cruz County, and the Monterey Peninsula — 51 cities in total — with dedicated landing pages for our highest-demand markets and flat-rate transfers to SFO, SJC, OAK, and MRY.",
  },
  {
    q: "Is this a limousine company or a black car service?",
    a: "Both, in practice. Premier Executive Chauffeur is a pre-arranged charter operator running luxury sedans, Chevrolet Suburban LT SUVs, and Mercedes-Benz Sprinter vans. Traditional stretch limousines are arranged through our parent operator on request.",
  },
  {
    q: "How is pricing structured?",
    a: "Airport and point-to-point transfers are flat rates confirmed in writing at booking. As-directed work is hourly from $130 per hour with a five-hour minimum. Tolls, parking, airport fees, and any pre-included gratuity are shown in the written quote.",
  },
  {
    q: "Can I book a limo for a group?",
    a: "Yes. The Mercedes-Benz Sprinter carries six to fourteen passengers depending on configuration, and larger movements are staged as multi-vehicle convoys managed by a single dispatcher so the group arrives together.",
  },
];

export const Route = createFileRoute("/limo-service/")({
  head: () => ({
    meta: [
      { title: "Limo Service Bay Area | Premier Executive Chauffeur" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Bay Area Limo Service — City by City" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Bay Area Limo Service — City by City" },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Limo Service", path: "/limo-service" },
      ]),
      {
        type: "application/ld+json" as const,
        children: ldJson({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              "@id": URL,
              url: URL,
              name: "Limo Service Bay Area",
              description: DESCRIPTION,
              inLanguage: "en-US",
              isPartOf: { "@id": `${SITE_URL}#website` },
              about: { "@id": `${SITE_URL}#business` },
              breadcrumb: { "@id": `${URL}#breadcrumb` },
            },
            {
              "@type": "ItemList",
              "@id": `${URL}#cities`,
              name: "Limo service markets",
              numberOfItems: LIMO_LANDINGS.length,
              itemListElement: LIMO_LANDINGS.map((l, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: l.keyword,
                url: `${URL}/${l.slug}`,
              })),
            },
            {
              "@type": "Service",
              "@id": `${URL}#service`,
              name: "Bay Area limo and chauffeur service",
              serviceType: "Limousine and chauffeur service",
              provider: { "@id": `${SITE_URL}#business` },
              hoursAvailable: hoursSpec24x7,
              availableChannel: {
                "@type": "ServiceChannel",
                serviceUrl: URL,
                servicePhone: SITE.phone,
                availableLanguage: "English",
              },
            },
            {
              ...faqJsonLd(HUB_FAQS),
              "@id": `${URL}#faq`,
              url: URL,
              isPartOf: { "@id": `${SITE_URL}#website` },
              about: { "@id": `${SITE_URL}#business` },
            },
          ],
        }),
      },
    ],
  }),
  component: LimoHub,
});

function LimoHub() {
  return (
    <>
      <section className="relative overflow-hidden border-b border-border">
        <img
          src={heroImg}
          alt="Black luxury sedan, Suburban LT SUV, and Mercedes-Benz Sprinter lined up for Bay Area limo service"
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/50 to-background" />
        <div className="relative container-x py-24 md:py-32 max-w-3xl">
          <Breadcrumbs items={[{ name: "Home", to: "/" }, { name: "Limo Service" }]} className="mb-4" />
          <p className="eyebrow mb-4">Limo service</p>
          <h1 className="text-4xl md:text-6xl mb-4 text-balance">Bay Area Limo Service, City by City</h1>
          <p className="text-muted-foreground text-lg mb-6">
            Flat-rate airport transfers, hourly charters from $130/hr, and Sprinter group travel — dispatched daily across Silicon Valley, San Francisco, and the East Bay.
          </p>
          <ContactButtons context="Bay Area limo service" />
        </div>
      </section>

      <div className="container-x py-16 space-y-16 max-w-3xl">
        <section className="space-y-4 text-muted-foreground">
          <p>
            Premier Executive Chauffeur runs pre-arranged limo and black car service across the San Francisco Bay Area. Every ride is reserved in advance, dispatched by a live team, and driven by a CPUC-licensed chauffeur — there is no surge pricing, no bidding for your car, and no stranger arriving in an unmarked hatchback. The pages below cover the markets where we run the most volume, with local corridors, drive times, and pricing for each.
          </p>
          <p>
            The fleet is three vehicle classes: a luxury sedan for up to three passengers, a Chevrolet Suburban LT SUV for up to six, and a Mercedes-Benz Sprinter for six to fourteen. Airport and point-to-point work is quoted as a written flat rate. As-directed itineraries — board days, wine tours, wedding timelines, investor tours — run hourly with a five-hour minimum and the same chauffeur for the full window.
          </p>
        </section>

        <section>
          <h2 className="text-2xl md:text-3xl mb-4">Airport dispatch meeting points</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              At SJC, sedan and SUV arrivals are handled inside baggage claim: the chauffeur waits in the correct terminal with the customer's name displayed. A Sprinter group uses the Charter Service pickup booth for Terminal A or Terminal B instead.
            </p>
            <p>
              At SFO International, limousine loading changed on August 18–19, 2026. Sedan and SUV passengers should leave through Door 2 or 3 on the A or G side and meet at the white-striped curbside section rather than the former Center Island zone.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl md:text-3xl mb-4">Choose your market</h2>
          <ul className="grid gap-4 sm:grid-cols-2">
            {LIMO_LANDINGS.map((l) => (
              <li key={l.slug} className="border border-border bg-surface p-5">
                <Link to="/limo-service/$city" params={{ city: l.slug }} className="font-display text-lg text-gold hover:underline">
                  {l.keyword}
                  <span className="sr-only"> — market landing page</span>
                </Link>
                <p className="text-sm text-muted-foreground mt-2">{l.hook}</p>
              </li>
            ))}
          </ul>
        </section>

        <HourlyRates heading="Published hourly limo rates" showFaqs={false} />

        <section>
          <h2 className="text-2xl md:text-3xl mb-4">Limo service FAQs</h2>
          <dl className="space-y-6">
            {HUB_FAQS.map((f) => (
              <div key={f.q}>
                <dt className="font-display text-lg text-foreground mb-1">{f.q}</dt>
                <dd className="text-muted-foreground">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section>
          <h2 className="text-2xl md:text-3xl mb-4">Keep exploring</h2>
          <ul className="grid gap-3 sm:grid-cols-2 text-sm">
            <li>
              <Link to="/sprinter-van-transportation" className="text-gold hover:underline">
                Sprinter van transportation
                <span className="sr-only"> — group page from the limo hub</span>
              </Link>
            </li>
            <li>
              <Link to="/service-areas" className="text-gold hover:underline">
                All 51 service areas
                <span className="sr-only"> — from the limo hub</span>
              </Link>
            </li>
            <li>
              <Link to="/airports" className="text-gold hover:underline">
                Airport transfer hub
                <span className="sr-only"> — from the limo hub</span>
              </Link>
            </li>
            <li>
              <Link to="/services" className="text-gold hover:underline">
                Full service catalog
                <span className="sr-only"> — from the limo hub</span>
              </Link>
            </li>
          </ul>
        </section>
      </div>

      <CTABand
        heading="Reserve your limo"
        sub={`Live staffed dispatch at ${SITE.phone}, or request an instant quote online.`}
      />
    </>
  );
}
